import { useQuery } from '@tanstack/react-query';
import { listDashboardDepartments } from '@/api/dashboards';
import { useAuth } from '@/auth/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type { DashboardDepartmentOption, DashboardFilterDef } from '@pm-next/shared-types';

interface BlockFiltersProps {
  filters: DashboardFilterDef[];
  toolArgs: Record<string, unknown>;
  disabled?: boolean;
  onChange: (toolArgs: Record<string, unknown>) => void;
}

const LATEST_MONTH = '__latest__';
const ALL_DEPARTMENTS = '__all__';
const MONTH_LOOKBACK = 24;

interface HierarchicalDepartmentOption extends DashboardDepartmentOption {
  label: string;
}

/** Parent-first department options with visual indentation for descendants. */
export function buildDepartmentHierarchy(
  departments: DashboardDepartmentOption[]
): HierarchicalDepartmentOption[] {
  const byId = new Map(departments.map((department) => [department.id, department]));
  const children = new Map<number | null, DashboardDepartmentOption[]>();

  for (const department of departments) {
    const parentId =
      department.parentId != null && byId.has(department.parentId) ? department.parentId : null;
    const siblings = children.get(parentId) ?? [];
    siblings.push(department);
    children.set(parentId, siblings);
  }

  for (const siblings of children.values()) {
    siblings.sort((a, b) => a.name.localeCompare(b.name));
  }

  const result: HierarchicalDepartmentOption[] = [];
  const visited = new Set<number>();
  const append = (department: DashboardDepartmentOption, depth: number) => {
    if (visited.has(department.id)) return;
    visited.add(department.id);
    result.push({
      ...department,
      // Fixed-width em spaces: 1 for first child level, 2 for grandchildren, etc.
      label: `${'\u2003'.repeat(depth)}${department.name}`,
    });
    for (const child of children.get(department.id) ?? []) append(child, depth + 1);
  };

  for (const root of children.get(null) ?? []) append(root, 0);
  // Include malformed/cyclic records without allowing recursion loops.
  for (const department of departments) append(department, 0);

  return result;
}

function formatMonthValue(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

function formatMonthLabel(year: number, monthIndex: number): string {
  return new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Recent months as YYYY-MM, newest first (payroll tools expect this format). */
export function buildMonthOptions(
  lookback = MONTH_LOOKBACK,
  now = new Date()
): Array<{
  value: string;
  label: string;
}> {
  const options: Array<{ value: string; label: string }> = [];
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  for (let i = 0; i < lookback; i++) {
    const d = new Date(Date.UTC(year, month - i, 1));
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    options.push({ value: formatMonthValue(y, m), label: formatMonthLabel(y, m) });
  }
  return options;
}

function MonthFilterSelect({
  filter,
  value,
  disabled,
  onChange,
}: {
  filter: DashboardFilterDef;
  value: string | null;
  disabled?: boolean;
  onChange: (value: string | null) => void;
}) {
  const options = buildMonthOptions();
  const selectValue = value && /^\d{4}-\d{2}$/.test(value) ? value : LATEST_MONTH;

  // Keep a custom YYYY-MM in the list if it falls outside the lookback window.
  const extra =
    selectValue !== LATEST_MONTH && !options.some((o) => o.value === selectValue)
      ? [{ value: selectValue, label: selectValue }]
      : [];

  return (
    <div className="min-w-[140px]">
      <Select
        disabled={disabled}
        value={selectValue}
        onValueChange={(v) => onChange(v === LATEST_MONTH ? null : v)}
      >
        <SelectTrigger className="h-8 text-xs" aria-label={filter.label}>
          <SelectValue placeholder={filter.label} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={LATEST_MONTH}>Latest payroll month</SelectItem>
          {[...extra, ...options].map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DepartmentFilterSelect({
  filter,
  value,
  disabled,
  departments,
  loading,
  onChange,
}: {
  filter: DashboardFilterDef;
  value: string | null;
  disabled?: boolean;
  departments: DashboardDepartmentOption[];
  loading?: boolean;
  onChange: (value: string | null) => void;
}) {
  const selectValue = value && value.trim() ? value : ALL_DEPARTMENTS;
  const hierarchy = buildDepartmentHierarchy(departments);
  const extras =
    selectValue !== ALL_DEPARTMENTS && !departments.some((d) => d.name === selectValue)
      ? [{ id: -1, name: selectValue, parentId: null, label: selectValue }]
      : [];

  return (
    <div className="min-w-[140px]">
      <Select
        disabled={disabled || loading}
        value={selectValue}
        onValueChange={(v) => onChange(v === ALL_DEPARTMENTS ? null : v)}
      >
        <SelectTrigger className="h-8 text-xs" aria-label={filter.label}>
          <SelectValue placeholder={loading ? 'Loading…' : filter.label} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_DEPARTMENTS}>All departments</SelectItem>
          {[...extras, ...hierarchy].map((dept) => (
            <SelectItem
              key={`${dept.id}-${dept.name}`}
              value={dept.name}
              className="whitespace-pre text-xs"
            >
              {dept.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function BlockFilters({ filters, toolArgs, disabled, onChange }: BlockFiltersProps) {
  const { selectedEmployer } = useAuth();
  const needsDepartments = filters.some((f) => f.type === 'department');
  const { data: departments = [], isLoading: departmentsLoading } = useQuery({
    queryKey: ['dashboard-departments', selectedEmployer?.id ?? 'none'],
    queryFn: listDashboardDepartments,
    enabled: needsDepartments,
    staleTime: 5 * 60 * 1000,
  });

  if (!filters.length) return null;

  const setValue = (key: string, value: string | number | null) => {
    onChange({ ...toolArgs, [key]: value });
  };

  return (
    <div className="flex flex-wrap gap-2">
      {filters.map((filter) => {
        const current =
          toolArgs[filter.key] ?? filter.defaultValue ?? (filter.type === 'days' ? 30 : '');

        if (filter.type === 'month') {
          const monthValue =
            typeof current === 'string' && /^\d{4}-\d{2}$/.test(current) ? current : null;
          return (
            <MonthFilterSelect
              key={filter.key}
              filter={filter}
              value={monthValue}
              disabled={disabled}
              onChange={(v) => setValue(filter.key, v)}
            />
          );
        }

        if (filter.type === 'department') {
          const deptValue = typeof current === 'string' && current.trim() ? current : null;
          return (
            <DepartmentFilterSelect
              key={filter.key}
              filter={filter}
              value={deptValue}
              disabled={disabled}
              departments={departments}
              loading={departmentsLoading}
              onChange={(v) => setValue(filter.key, v)}
            />
          );
        }

        if (filter.options && filter.options.length > 0) {
          return (
            <div key={filter.key} className="min-w-[120px]">
              <Select
                disabled={disabled}
                value={String(current ?? '')}
                onValueChange={(v) => {
                  const opt = filter.options!.find((o) => String(o.value) === v);
                  setValue(filter.key, opt ? opt.value : v);
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={filter.label} />
                </SelectTrigger>
                <SelectContent>
                  {filter.options.map((opt) => (
                    <SelectItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }

        return (
          <Input
            key={filter.key}
            className="h-8 w-[120px] text-xs"
            disabled={disabled}
            value={current == null ? '' : String(current)}
            onChange={(e) => {
              const raw = e.target.value;
              const num = Number(raw);
              setValue(filter.key, raw === '' ? null : Number.isNaN(num) ? raw : num);
            }}
            placeholder={filter.label}
          />
        );
      })}
    </div>
  );
}
